import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface Report {
  id: string;
  reporter_id: string | null;
  target_type: string | null;
  target_id: string | null;
  reason: string | null;
  status: string | null;
  created_at: string;
}

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="mb-6">
      <h1 className="font-heading font-bold text-2xl text-foreground-50">{title}</h1>
      <p className="text-sm text-foreground-400 mt-1">{subtitle}</p>
    </div>
  );
}

function StatusPill({ value }: { value: string }) {
  const map: Record<string, string> = {
    pending: 'bg-accent-500/20 text-accent-300',
    resolved: 'bg-secondary-500/20 text-secondary-300',
    dismissed: 'bg-foreground-800/80 text-foreground-100',
  };
  const labels: Record<string, string> = {
    pending: 'En attente',
    resolved: 'Résolu',
    dismissed: 'Rejeté',
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
        map[value] || 'bg-background-200/60 text-foreground-500'
      }`}
    >
      {labels[value] || value || '—'}
    </span>
  );
}

function ReportsSection() {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('reports')
        .select('*')
        .order('created_at', { ascending: false });
      if (err) throw err;
      setReports((data as Report[]) || []);
    } catch {
      setError("Impossible de charger les signalements. Vérifiez vos droits d'administrateur.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id: string, status: string) => {
    setBusyId(id);
    setError('');
    setSuccess('');
    try {
      const { error: err } = await supabase.from('reports').update({ status }).eq('id', id);
      if (err) throw err;
      setReports((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      setSuccess('Signalement mis à jour.');
    } catch {
      setError("Impossible de mettre à jour ce signalement.");
    } finally {
      setBusyId(null);
    }
  };

  const removeReport = async (id: string) => {
    setBusyId(id);
    setError('');
    setSuccess('');
    try {
      const { error: err } = await supabase.from('reports').delete().eq('id', id);
      if (err) throw err;
      setReports((prev) => prev.filter((r) => r.id !== id));
      setSuccess('Signalement supprimé.');
    } catch {
      setError('Impossible de supprimer ce signalement.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <SectionHeader
        title="Signalements"
        subtitle="Examiner et traiter les signalements envoyés par les utilisateurs."
      />

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-accent-100/10 border border-accent-400/20 mb-4">
          <i className="ri-error-warning-line text-accent-400 mt-0.5 shrink-0" />
          <p className="text-sm text-accent-400">{error}</p>
        </div>
      )}
      {success && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-secondary-500/10 border border-secondary-300/20 mb-4">
          <i className="ri-checkbox-circle-line text-secondary-300 mt-0.5 shrink-0" />
          <p className="text-sm text-secondary-300">{success}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-foreground-400">
          <i className="ri-loader-4-line animate-spin text-2xl mr-2" />
          Chargement des signalements…
        </div>
      ) : reports.length === 0 ? (
        <p className="text-foreground-400">Aucun signalement pour le moment.</p>
      ) : (
        <div className="bg-background-100 border border-background-200/70 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200/70 text-left text-foreground-400">
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Cible</th>
                  <th className="px-4 py-3 font-medium">Motif</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Statut</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-background-200/50 hover:bg-background-200/30 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <span className="text-foreground-500 capitalize">
                        {r.target_type || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-foreground-400 font-mono text-xs">
                        {(r.target_id || '').slice(0, 8)}…
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-foreground-50 line-clamp-1 max-w-xs">
                        {r.reason || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground-500 whitespace-nowrap">
                      {new Date(r.created_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill value={r.status || 'pending'} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {r.status !== 'resolved' && (
                          <button
                            onClick={() => updateStatus(r.id, 'resolved')}
                            disabled={busyId === r.id}
                            title="Marquer résolu"
                            className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-500/20 text-secondary-300 hover:bg-secondary-500/30 transition-colors disabled:opacity-50"
                          >
                            <i className="ri-check-line text-sm" />
                          </button>
                        )}
                        {r.status !== 'dismissed' && (
                          <button
                            onClick={() => updateStatus(r.id, 'dismissed')}
                            disabled={busyId === r.id}
                            title="Rejeter"
                            className="w-8 h-8 flex items-center justify-center rounded-md bg-background-200/60 text-foreground-400 hover:text-foreground-50 hover:bg-background-300/60 transition-colors disabled:opacity-50"
                          >
                            <i className="ri-close-line text-sm" />
                          </button>
                        )}
                        <button
                          onClick={() => removeReport(r.id)}
                          disabled={busyId === r.id}
                          title="Supprimer"
                          className="w-8 h-8 flex items-center justify-center rounded-md bg-foreground-800/80 text-foreground-100 hover:bg-foreground-700 transition-colors disabled:opacity-50"
                        >
                          {busyId === r.id ? (
                            <i className="ri-loader-4-line animate-spin text-sm" />
                          ) : (
                            <i className="ri-delete-bin-6-line text-sm" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportsSection;